import React, { useState, useEffect, useMemo, useContext } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { ScatterChart } from '@mui/x-charts/ScatterChart';
import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';
import { useItemTooltip } from '@mui/x-charts';
import { BusinessContext } from '../context/BusinessContext';
import { CustomChartTooltip } from './common/ChartDefaults';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { BarChart } from '@mui/x-charts/BarChart';

// Custom tooltip using useItemTooltip hook (MUI X-Charts recommended way)
function CustomScatterTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  
  const data = payload[0].payload;
  
  const formatCurrency = (value) => {
    if (value == null) return 'N/A';
    return `£${value.toLocaleString('en-GB')}`;
  };
  
  const formatRatingImpact = (value) => {
    if (value == null) return 'N/A';
    const formatted = value.toFixed(4);
    return value > 0 ? `+${formatted} ★` : `${formatted} ★`;
  };
  
  return (
    <Box sx={{ 
      bgcolor: 'background.paper', 
      p: 1, 
      border: 1, 
      borderColor: 'divider',
      borderRadius: 1,
      boxShadow: 1
    }}>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
        {payload[0].seriesId || 'Point'}
      </Typography>
      <Typography variant="body2">
        Revenue: {formatCurrency(data.x)}
      </Typography>
      <Typography variant="body2">
        Rating Impact: {formatRatingImpact(data.y)}
      </Typography>
    </Box>
  );
}

// Minimal custom tooltip for testing
function DummyTooltip() {
  return (
    <div style={{ background: 'red', color: 'white', padding: 8, borderRadius: 4 }}>
      CUSTOM TOOLTIP
    </div>
  );
}

// Add a simple custom tooltip for the dummy chart
function DummyScatterTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  
  const point = payload[0].payload;
  const formatX = (value) => {
    if (value == null) return 'N/A';
    const abs = Math.abs(Math.round(value));
    const formatted = abs.toLocaleString();
    return value > 0 ? `+£${formatted}` : `£${formatted}`;
  };
  
  const formatY = (value) => {
    if (value == null) return 'N/A ★';
    const formatted = value.toFixed(4);
    return value > 0 ? `+${formatted} ★` : `${formatted} ★`;
  };
  
  return (
    <div style={{
      backgroundColor: 'rgba(10, 37, 64, 0.9)',
      padding: '10px 12px',
      fontSize: '0.85rem',
      borderRadius: '8px',
      boxShadow: '0 5px 15px rgba(0,0,0,0.15)',
      border: 'none',
      color: '#E5E8EC',
      maxWidth: 'none',
      whiteSpace: 'nowrap'
    }}>
      <div style={{ marginBottom: '5px', fontSize: '0.85rem' }}>Dummy</div>
      <div>X: {formatX(point.x)}</div>
      <div>Y: {formatY(point.y)}</div>
    </div>
  );
}

// Format rating impact to 3 significant figures or first significant figure if more than 3 decimal places
const formatRatingImpactValue = (value) => {
  if (value == null || value === 0) return 0;
  
  const absValue = Math.abs(value);
  
  // If value is very small (more than 3 decimal places), show only first significant figure
  if (absValue < 0.001) {
    const firstSigFig = parseFloat(value.toPrecision(1));
    return firstSigFig;
  }
  
  // Otherwise, show 3 significant figures
  const threeSigFigs = parseFloat(value.toPrecision(3));
  return threeSigFigs;
};

const PerformanceAnalysisChart = ({ selectedPeriod }) => {
  const { currencySymbol } = useContext(BusinessContext);
  const [analysisData, setAnalysisData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    if (selectedPeriod) {
      fetchAnalysisData();
    }
  }, [selectedPeriod]);

  const fetchAnalysisData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/sales/periods/${selectedPeriod.id}/performance-analysis`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setAnalysisData(data);
      } else {
        setError('Failed to fetch performance analysis data');
      }
    } catch (err) {
      setError('Error fetching performance analysis data');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Generate color based on positive/negative impact ratio

  const getColorForImpactRatio = (positiveImpact, negativeImpact) => {
    const totalAbsImpact = Math.abs(positiveImpact) + Math.abs(negativeImpact);
    if (totalAbsImpact === 0) return 'rgba(97, 97, 97, 0.9)'; // Grey for no impact
    const positiveRatio = Math.abs(positiveImpact) / totalAbsImpact;
    if (positiveRatio >= 0.8) return 'rgba(56, 142, 60, 0.9)'; // Green (80%+ positive)
    if (positiveRatio >= 0.6) return 'rgba(139, 195, 74, 0.9)'; // Light green (60-80% positive)
    if (positiveRatio >= 0.4) return 'rgba(255, 152, 0, 0.9)'; // Orange (40-60% mixed)
    if (positiveRatio >= 0.2) return 'rgba(255, 87, 34, 0.9)'; // Orange-red (20-40% positive)
    return 'rgba(211, 47, 47, 0.5)'; // Red (0-20% positive)
  };

  // Process data for scatter plot
  const chartData = useMemo(() => {
    if (!analysisData.length) return [];
    // Find max insight count for size normalization
    const maxInsightCount = Math.max(...analysisData.map(item => item.review_count || 1));
    
    // Debug logging to understand the issue
    console.log('Debug: Bubble sizing data');
    console.log('Max insight count:', maxInsightCount);
    analysisData.forEach((item, index) => {
      const reviewCount = item.review_count || 1;
      const normalizedSize = Math.max(1, Math.min(24, 
        1 + (Math.sqrt((reviewCount) / maxInsightCount) * 23)
      ));
      console.log(`Item ${index}: ${item.subject} - Review count: ${reviewCount}, Normalized size: ${normalizedSize}`);
    });
    
    // Create individual series for each data point
    return analysisData.map((item, index) => {
      // Calculate normalized size (minimum 1, maximum 24 for better scaling - square root for linear area)
      const normalizedSize = Math.max(1, Math.min(24, 
        1 + (Math.sqrt((item.review_count || 1) / maxInsightCount) * 23)
      ));
      // Calculate color based on positive/negative ratio
      const color = getColorForImpactRatio(
        item.positive_impact || 0, 
        item.negative_impact || 0
      );
      return {
        label: item.subject, // Each bubble shows its subject name
        color: color,
        markerSize: normalizedSize,
        data: [{
          x: item.total_revenue,
          y: formatRatingImpactValue(item.rating_impact),
          id: index,
          label: item.subject,
          category: item.category,
          qty_sold: item.qty_sold,
          insight_count: item.review_count, // Renamed for clarity - this is actually insight count
          positive_impact: item.positive_impact,
          negative_impact: item.negative_impact,
          currencySymbol: currencySymbol
        }]
      };
    });
  }, [analysisData, currencySymbol]);

  // Calculate chart bounds with padding and median revenue for quadrant lines
  const { xMax, yMin, yMax, medianRevenue } = useMemo(() => {
    if (!analysisData.length) return { xMax: 100, yMin: -1, yMax: 1, medianRevenue: 50 };
    const revenues = analysisData.map(item => item.total_revenue);
    const impacts = analysisData.map(item => formatRatingImpactValue(item.rating_impact));
    const maxRevenue = Math.max(...revenues);
    const minImpact = Math.min(...impacts);
    const maxImpact = Math.max(...impacts);
    // Calculate median revenue for vertical quadrant line
    const sortedRevenues = [...revenues].sort((a, b) => a - b);
    const medianRevenue = sortedRevenues[Math.floor(sortedRevenues.length / 2)];
    // Add 10% padding based on actual data range
    const revenuePadding = maxRevenue * 0.1;
    const impactRange = maxImpact - minImpact;
    const impactPadding = impactRange * 0.1;
    return {
      xMax: maxRevenue + revenuePadding,
      yMin: minImpact - impactPadding,
      yMax: maxImpact + impactPadding,
      medianRevenue
    };
  }, [analysisData]);

  // Process data for category-based scatter plot
  const categoryChartData = useMemo(() => {
    if (!analysisData.length) return [];
    
    // Group data by category
    const categoryMap = {};
    analysisData.forEach((item) => {
      const categoryKey = item.category.toLowerCase();
      if (!categoryMap[categoryKey]) {
        categoryMap[categoryKey] = {
          category: item.category, // Keep original case
          total_revenue: 0,
          rating_impact_sum: 0,
          rating_impact_count: 0,
          insight_count: 0,
          positive_impact_sum: 0,
          negative_impact_sum: 0,
          items: []
        };
      }
      
      const categoryData = categoryMap[categoryKey];
      categoryData.total_revenue += item.total_revenue || 0;
      categoryData.rating_impact_sum += item.rating_impact || 0;
      categoryData.rating_impact_count += 1;
      categoryData.insight_count += item.review_count || 0;
      categoryData.positive_impact_sum += item.positive_impact || 0;
      categoryData.negative_impact_sum += item.negative_impact || 0;
      categoryData.items.push(item);
    });

    // Convert to array and calculate averages
    const categoryArray = Object.values(categoryMap).map(categoryData => ({
      category: categoryData.category,
      total_revenue: categoryData.total_revenue,
      rating_impact: categoryData.rating_impact_count > 0 ? categoryData.rating_impact_sum / categoryData.rating_impact_count : 0,
      insight_count: categoryData.insight_count,
      positive_impact: categoryData.positive_impact_sum,
      negative_impact: categoryData.negative_impact_sum
    }));

    // Find max insight count for size normalization
    const maxInsightCount = Math.max(...categoryArray.map(item => item.insight_count || 1));
    
    // Create individual series for each category
    return categoryArray.map((item, index) => {
      // Calculate normalized size (minimum 1, maximum 24 for better scaling - square root for linear area)
      const normalizedSize = Math.max(1, Math.min(24, 
        1 + (Math.sqrt((item.insight_count || 1) / maxInsightCount) * 23)
      ));
      // Calculate color based on positive/negative ratio
      const color = getColorForImpactRatio(
        item.positive_impact || 0, 
        item.negative_impact || 0
      );
      return {
        label: item.category, // Each bubble shows its category name
        color: color,
        markerSize: normalizedSize,
        data: [{
          x: item.total_revenue,
          y: formatRatingImpactValue(item.rating_impact),
          id: index,
          label: item.category,
          insight_count: item.insight_count,
          positive_impact: item.positive_impact,
          negative_impact: item.negative_impact,
          currencySymbol: currencySymbol
        }]
      };
    });
  }, [analysisData, currencySymbol]);

  // Calculate category chart bounds
  const { xMax: categoryXMax, yMin: categoryYMin, yMax: categoryYMax } = useMemo(() => {
    if (!categoryChartData.length) return { xMax: 100, yMin: -1, yMax: 1 };
    const revenues = categoryChartData.map(series => series.data[0].x);
    const impacts = categoryChartData.map(series => series.data[0].y);
    const maxRevenue = Math.max(...revenues);
    const minImpact = Math.min(...impacts);
    const maxImpact = Math.max(...impacts);
    // Add 10% padding based on actual data range
    const revenuePadding = maxRevenue * 0.1;
    const impactRange = maxImpact - minImpact;
    const impactPadding = impactRange * 0.1;
    return {
      xMax: maxRevenue + revenuePadding,
      yMin: minImpact - impactPadding,
      yMax: maxImpact + impactPadding
    };
  }, [categoryChartData]);

  if (!selectedPeriod) {
    return null;
  }

  return (
    <Card sx={{ mt: 4 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AssessmentIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">
            Performance Analysis by Subject
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Revenue vs Rating Impact scatter plot. Items in the top-right quadrant (high revenue + positive impact) are your "Stars". 
          Bubble sizes reflect insight volume (more insights = larger bubbles).
        </Typography>
        {/* Custom Color Legend - moved above chart */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Color Legend (Sentiment Ratio):
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#388e3c' }} />
              <Typography variant="caption">80%+ Positive</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#8bc34a' }} />
              <Typography variant="caption">60-80% Positive</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ff9800' }} />
              <Typography variant="caption">40-60% Mixed</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ff5722' }} />
              <Typography variant="caption">20-40% Positive</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#d32f2f' }} />
              <Typography variant="caption">0-20% Positive</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#616161' }} />
              <Typography variant="caption">No Impact</Typography>
            </Box>
          </Box>
        </Box>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography variant="body2" color="error" sx={{ textAlign: 'center', py: 4 }}>
            {error}
          </Typography>
        ) : analysisData.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No performance data available for this period.
          </Typography>
        ) : (
          <>
            <Box sx={{ height: 500, position: 'relative' }}>
              {/* Quadrant Labels */}
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                pointerEvents: 'none',
                zIndex: 10
              }}>
                <Typography variant="caption" sx={{
                  position: 'absolute',
                  top: '12%',
                  right: '10%',
                  fontWeight: 'bold',
                  color: 'success.main',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1
                }}>
                  ⭐ STARS
                </Typography>
                
                <Typography variant="caption" sx={{
                  position: 'absolute',
                  top: '12%',
                  left: '15%',
                  fontWeight: 'bold',
                  color: 'info.main',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1
                }}>
                  💎 HIDDEN GEMS
                </Typography>
                
                <Typography variant="caption" sx={{
                  position: 'absolute',
                  bottom: '25%',
                  right: '10%',
                  fontWeight: 'bold',
                  color: 'warning.main',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1
                }}>
                  ⚠️ PROBLEM AREAS
                </Typography>
                
                <Typography variant="caption" sx={{
                  position: 'absolute',
                  bottom: '25%',
                  left: '15%',
                  fontWeight: 'bold',
                  color: 'error.main',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1
                }}>
                  🗑️ CONSIDER REMOVING
                </Typography>
              </Box>
              
              <ScatterChart
                sx={{
                  width: '100%',
                  height: '100%',
                  '.MuiChartsAxis-label': {
                    fill: '#546E7A',
                    fontWeight: 500,
                    fontSize: isSmallScreen ? 10 : 12
                  },
                  '.MuiChartsAxis-tickLabel': {
                    fill: '#546E7A',
                    fontSize: isSmallScreen ? 9 : 11
                  }
                }}
                margin={isSmallScreen ? 
                  { top: 8, bottom: 75, left: 120, right: 20 } : 
                  { top: 15, bottom: 95, left: 110, right: 40 }
                }
                series={chartData}
                xAxis={[{
                  label: `Revenue (${currencySymbol})`,
                  min: 0,
                  max: xMax,
                  tickLabelStyle: {
                    fontSize: isSmallScreen ? 9 : 11,
                  },
                  valueFormatter: (value) => `${currencySymbol}${value.toLocaleString('en-GB')}`
                }]}
                yAxis={[{
                  label: 'Rating Impact',
                  min: yMin,
                  max: yMax,
                  tickLabelStyle: {
                    fontSize: isSmallScreen ? 9 : 11,
                  },
                  valueFormatter: (value) => {
                    // Dynamic formatting based on the magnitude of the values
                    const absValue = Math.abs(value);
                    if (absValue === 0) return '0';
                    if (absValue >= 1) return value > 0 ? `+${value.toFixed(2)}` : value.toFixed(2);
                    if (absValue >= 0.1) return value > 0 ? `+${value.toFixed(3)}` : value.toFixed(3);
                    if (absValue >= 0.01) return value > 0 ? `+${value.toFixed(4)}` : value.toFixed(4);
                    if (absValue >= 0.001) return value > 0 ? `+${value.toFixed(5)}` : value.toFixed(5);
                    return value > 0 ? `+${value.toFixed(6)}` : value.toFixed(6);
                  }
                }]}
                grid={{ vertical: true, horizontal: true }}
                slotProps={{
                  legend: { hidden: true }
                }}
              >
                <ChartsReferenceLine x={xMax / 2} lineStyle={{ strokeDasharray: '5 5', stroke: 'black' }} />
                <ChartsReferenceLine y={0} lineStyle={{ strokeDasharray: '5 5', stroke: 'black' }} />
              </ScatterChart>
            </Box>
          </>
        )}
        
        {/* Category Performance Analysis Chart */}
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <AssessmentIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              Performance Analysis by Category
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Revenue vs Rating Impact by category. Categories aggregate all subjects within them.
            Bubble sizes reflect insight volume (more insights = larger bubbles).
          </Typography>
          
          {/* Category Color Legend */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              Color Legend (Sentiment Ratio):
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#388e3c' }} />
                <Typography variant="caption">80%+ Positive</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#8bc34a' }} />
                <Typography variant="caption">60-80% Positive</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ff9800' }} />
                <Typography variant="caption">40-60% Mixed</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ff5722' }} />
                <Typography variant="caption">20-40% Positive</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#d32f2f' }} />
                <Typography variant="caption">0-20% Positive</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#616161' }} />
                <Typography variant="caption">No Impact</Typography>
              </Box>
            </Box>
          </Box>
          
          {categoryChartData.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No category data available for this period.
            </Typography>
          ) : (
            <Box sx={{ height: 500, position: 'relative' }}>
              {/* Category Quadrant Labels */}
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                pointerEvents: 'none',
                zIndex: 10
              }}>
                <Typography variant="caption" sx={{
                  position: 'absolute',
                  top: '12%',
                  right: '10%',
                  fontWeight: 'bold',
                  color: 'success.main',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1
                }}>
                  ⭐ STARS
                </Typography>
                
                <Typography variant="caption" sx={{
                  position: 'absolute',
                  top: '12%',
                  left: '15%',
                  fontWeight: 'bold',
                  color: 'info.main',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1
                }}>
                  💎 HIDDEN GEMS
                </Typography>
                
                <Typography variant="caption" sx={{
                  position: 'absolute',
                  bottom: '25%',
                  right: '10%',
                  fontWeight: 'bold',
                  color: 'warning.main',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1
                }}>
                  ⚠️ PROBLEM AREAS
                </Typography>
                
                <Typography variant="caption" sx={{
                  position: 'absolute',
                  bottom: '25%',
                  left: '15%',
                  fontWeight: 'bold',
                  color: 'error.main',
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1
                }}>
                  🗑️ CONSIDER REMOVING
                </Typography>
              </Box>
              
              <ScatterChart
                sx={{
                  width: '100%',
                  height: '100%',
                  '.MuiChartsAxis-label': {
                    fill: '#546E7A',
                    fontWeight: 500,
                    fontSize: isSmallScreen ? 10 : 12
                  },
                  '.MuiChartsAxis-tickLabel': {
                    fill: '#546E7A',
                    fontSize: isSmallScreen ? 9 : 11
                  }
                }}
                margin={isSmallScreen ? 
                  { top: 8, bottom: 75, left: 120, right: 20 } : 
                  { top: 15, bottom: 95, left: 110, right: 40 }
                }
                series={categoryChartData}
                xAxis={[{
                  label: `Revenue (${currencySymbol})`,
                  min: 0,
                  max: categoryXMax,
                  tickLabelStyle: {
                    fontSize: isSmallScreen ? 9 : 11,
                  },
                  valueFormatter: (value) => `${currencySymbol}${value.toLocaleString('en-GB')}`
                }]}
                yAxis={[{
                  label: 'Rating Impact',
                  min: categoryYMin,
                  max: categoryYMax,
                  tickLabelStyle: {
                    fontSize: isSmallScreen ? 9 : 11,
                  },
                  valueFormatter: (value) => {
                    // Dynamic formatting based on the magnitude of the values
                    const absValue = Math.abs(value);
                    if (absValue === 0) return '0';
                    if (absValue >= 1) return value > 0 ? `+${value.toFixed(2)}` : value.toFixed(2);
                    if (absValue >= 0.1) return value > 0 ? `+${value.toFixed(3)}` : value.toFixed(3);
                    if (absValue >= 0.01) return value > 0 ? `+${value.toFixed(4)}` : value.toFixed(4);
                    if (absValue >= 0.001) return value > 0 ? `+${value.toFixed(5)}` : value.toFixed(5);
                    return value > 0 ? `+${value.toFixed(6)}` : value.toFixed(6);
                  }
                }]}
                grid={{ vertical: true, horizontal: true }}
                slotProps={{
                  legend: { hidden: true }
                }}
              >
                <ChartsReferenceLine x={categoryXMax / 2} lineStyle={{ strokeDasharray: '5 5', stroke: 'black' }} />
                <ChartsReferenceLine y={0} lineStyle={{ strokeDasharray: '5 5', stroke: 'black' }} />
              </ScatterChart>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default PerformanceAnalysisChart; 